/**
 * SolarCoronaPipeline — see FRAG_SRC header.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SolarCoronaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash11(float n) {
  return fract(sin(n * 12.9898) * 43758.5453);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.02, 0.06), vec3(0.01, 0.01, 0.02), smoothstep(0.0, 0.7, r));
  vec3 col = bg;
  // The sun at the center, eclipsed (dark moon in front).
  float sunR = 0.30;
  // The sun is bright behind the moon.
  float sunD = length(p);
  float sunMask = 1.0 - smoothstep(sunR - 0.05, sunR + 0.05, sunD);
  vec3 sunColor = vec3(0.95, 0.85, 0.55);
  col = mix(col, sunColor, sunMask * 0.7);
  // The moon (eclipsing).
  float moonR = 0.32;
  float moonMask = 1.0 - smoothstep(moonR - 0.005, moonR + 0.005, sunD);
  col = mix(col, vec3(0.01, 0.01, 0.02), moonMask);
  // The corona: bright streamers radiating outward from the moon.
  for (int i = 0; i < 32; i++) {{
    float fi = float(i);
    float ang = fi * 0.196;
    vec2 dir = vec2(cos(ang), sin(ang));
    float along = dot(p, dir);
    if (along < moonR || along > 0.6) continue;
    // Streamer length and width.
    float streamerLen = 0.20 + 0.15 * hash11(fi);
    float streamerW = 0.005 + 0.015 * (1.0 - along / 0.6);
    vec2 perp = p - dir * along;
    float dd = length(perp);
    float streamerMask = 1.0 - smoothstep(0.0, streamerW, dd);
    streamerMask *= step(moonR, along) * step(along, moonR + streamerLen);
    vec3 coronaCol = vec3(0.95, 0.75, 0.45);
    col += coronaCol * streamerMask * (0.5 + 0.5 * high);
  }}
  // Bright ring around the moon (the inner corona).
  float ringD = abs(sunD - moonR);
  float ringMask = exp(-ringD * 30.0);
  col += vec3(0.95, 0.85, 0.55) * ringMask * 0.6;
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);


}
`;

export function computeSolarCoronaBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeSolarCoronaRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSolarCoronaPipeline(gl: WebGL2RenderingContext): SolarCoronaPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSolarCorona(
  gl: WebGL2RenderingContext,
  pipe: SolarCoronaPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
